// frontend/modules/notification-config/views/NotificationConfigListView.tsx
import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Typography, 
  Input, 
  Select, 
  Popconfirm,
  Badge,
  Tooltip,
  Row,
  Col,
  Divider
} from 'antd';
import { 
  PlusOutlined, 
  SearchOutlined, 
  EditOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  FilterOutlined,
  ReloadOutlined,
  MailOutlined,
  MessageOutlined,
  WhatsAppOutlined
} from '@ant-design/icons';
import { useNotificationConfigs } from '../hooks/useNotificationConfigs';
import { NotificationConfig } from '../types/notification-config.types';
import { NotificationConfigFormModal } from '../components/NotificationConfigFormModal';
import { NotificationTestModal } from '../components/NotificationTestModal';
import NotificationConfigImportExport from '../components/NotificationConfigImportExport';
import { NotificationPlatforms } from '@/shared/api/model';

const { Title } = Typography;
const { Option } = Select;

export const NotificationConfigListView: React.FC = () => {
  const { 
    filteredConfigurations,
    isLoading,
    filters,
    setFilters,
    clearFilters,
    setSelectedConfiguration,
    setEditModalVisible,
    setTestModalVisible,
    deleteConfig,
    refetchConfigurations,
    exportConfigs,
    importConfigsFromFile,
    importResult
  } = useNotificationConfigs();
  
  // Platform icon mapping
  const getPlatformIcon = (platform: NotificationPlatforms) => {
    switch (platform) {
      case NotificationPlatforms.EMAIL:
        return <MailOutlined />;
      case NotificationPlatforms.SMS:
        return <MessageOutlined />;
      case NotificationPlatforms.WHATS_APP:
        return <WhatsAppOutlined />;
      default:
        return null;
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      render: (text: string) => <strong>{text}</strong>
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Platform',
      dataIndex: 'platform',
      key: 'platform',
      render: (platform: NotificationPlatforms) => (
        <Tag icon={getPlatformIcon(platform)}>
          {platform}
        </Tag>
      )
    },
    {
      title: 'Template',
      dataIndex: 'templateName',
      key: 'templateName'
    },
    {
      title: 'Context Script',
      dataIndex: 'contextScriptName',
      key: 'contextScriptName'
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) => (
        active ? 
          <Badge status="success" text="Active" /> : 
          <Badge status="default" text="Inactive" />
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_:any, record: NotificationConfig) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Test">
            <Button 
              type="text" 
              icon={<PlayCircleOutlined />}
              onClick={() => handleTest(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this notification configuration?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button 
                type="text" 
                danger 
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  // Handlers
  const handleEdit = (config: NotificationConfig) => {
    setSelectedConfiguration(config);
    setEditModalVisible(true);
  };

  const handleAdd = () => {
    setSelectedConfiguration(null);
    setEditModalVisible(true);
  };

  const handleTest = (config: NotificationConfig) => {
    setSelectedConfiguration(config);
    setTestModalVisible(true);
  };

  const handleDelete = async (id?: string) => {
    if (id) {
      await deleteConfig(id);
    }
  };

  return (
    <div className="p-6">
      <Title level={2}>Notification Configurations</Title>
      
      <Card className="mb-4">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder="Search by code or name"
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value })}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Filter by platform"
              style={{ width: '100%' }}
              value={filters.platform}
              onChange={(value) => setFilters({ platform: value })}
              allowClear
              suffixIcon={<FilterOutlined />}
            >
              <Option value={NotificationPlatforms.EMAIL}>Email</Option>
              <Option value={NotificationPlatforms.SMS}>SMS</Option>
              <Option value={NotificationPlatforms.WHATS_APP}>WhatsApp</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Select
              placeholder="Filter by status"
              style={{ width: '100%' }}
              value={filters.active}
              onChange={(value) => setFilters({ active: value })}
              allowClear
              suffixIcon={<FilterOutlined />}
            >
              <Option value={true}>Active</Option>
              <Option value={false}>Inactive</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Space>
              <Button 
                type="default" 
                icon={<ReloadOutlined />}
                onClick={() => {
                  clearFilters();
                  refetchConfigurations();
                }}
              >
                Reset
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleAdd}
              >
                Add Configuration
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      
      <Card>
        <NotificationConfigImportExport 
          onExport={exportConfigs} 
          onImport={importConfigsFromFile} 
          isLoading={isLoading}
          importResult={importResult} 
        />
        <Divider />
        <Table
          dataSource={filteredConfigurations}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} configurations`
          }}
        />
      </Card>
      
      <NotificationConfigFormModal />
      <NotificationTestModal />
    </div>
  );
};
